/**
 * Duplicate Accessory — the "Duplicate into Section" list's rows.
 *
 * On a HYBRID unit a row is a `row:level` CELL rather than a tunnel (requested
 * 2026-11-02): a cell holds two back-to-back tunnels, and a row per tunnel
 * printed them as two lines (`1:1:1 Plenum` over `1:1:2 Plenum`) that read as
 * unrelated tunnels. A row is therefore the shape a standard AHU's row has
 * always had — the tunnel label, then one select of what the accessory can be
 * duplicated into — except that on a hybrid the select spans both positions of
 * the cell. An option is one (position, section) pair, so picking one still
 * resolves to exactly one tunnel id and one section id, which is all the
 * duplication itself ever reads. Options are labelled with the section's own
 * name on both kinds of unit; the position is carried, not printed.
 *
 * Anything NOT flagged hybrid keeps a row per tunnel, including a unit whose
 * tunnels happen to share a cell because the old Add Section flow mis-addressed
 * them (AHU 6732) — the same decision ConfigureTunnel.tsx:197-248 and
 * TabAhu.tsx:320 record for the tunnel list, so all three agree about what a
 * row is.
 *
 * Pure so the row maths can be tested without the popup's RTK Query loads;
 * `DuplicateAccessoryPopup.tsx` supplies the section lists from the same
 * `csa_available_sections` filter it gates the rows with.
 */
import { compareTunnelAddress, formatRowLevel } from '../addressing';
import type { IAddressable } from '../addressing';

export interface ITunnelCell<T> {
  /** The row's key: `"row:level"` on a hybrid, `"t<id>"` per tunnel otherwise. */
  cell: string;
  /** Every tunnel on the row, in `row:level:position` order. */
  tunnels: T[];
}

/** One line of a row's select: a section, and the position it sits in. */
export interface ICellOption<T, S> {
  tunnel: T;
  section: S;
}

export interface ICellOptions<T, S> {
  options: ICellOption<T, S>[];
  /** No position on the row can take the accessory — the row is non-clickable. */
  gated: boolean;
}

/**
 * Group tunnels into `row:level` cells, in address order — so a cell's two
 * positions are listed position 1 first whatever order the server returned
 * them in (`AHUTunnels.Meta.ordering = ['id']` is creation order, and a cell's
 * second position is not always the later row).
 */
export function groupTunnelsByCell<T extends IAddressable>(tunnels: T[]): ITunnelCell<T>[] {
  const byCell = new Map<string, T[]>();
  for (const tunnel of [...tunnels].sort(compareTunnelAddress)) {
    const cell = formatRowLevel(tunnel);
    const list = byCell.get(cell);
    if (list) list.push(tunnel);
    else byCell.set(cell, [tunnel]);
  }
  return [...byCell.entries()].map(([cell, list]) => ({ cell, tunnels: list }));
}

/**
 * The dialog's rows. Only a unit the board has FLAGGED hybrid is grouped by
 * cell; everything else keeps the row-per-tunnel list it has always had, in
 * the order the tunnels arrived.
 */
export function duplicateTargetCells<T extends IAddressable & { id: number }>(
  tunnels: T[],
  isHybrid: boolean | undefined
): ITunnelCell<T>[] {
  if (!isHybrid) return tunnels.map((tunnel) => ({ cell: `t${tunnel.id}`, tunnels: [tunnel] }));
  return groupTunnelsByCell(tunnels);
}

/**
 * What a row's select offers: every eligible section of every position on the
 * row, position by position.
 *
 * `filterTunnelSections` (duplicate_accessory.js:81-108) — a row with no
 * eligible section anywhere is non-clickable but still shows everything it
 * holds, rather than going blank.
 */
export function cellOptions<T extends { id: number }, S>(
  cellTunnels: T[],
  eligibleSections: (tunnelId: number) => S[],
  allSections: (tunnelId: number) => S[],
  /** The accessory's own section and position, when this row holds it. */
  source?: { section?: S | null; tunnelId?: number | null }
): ICellOptions<T, S> {
  const perTunnel = cellTunnels.map((tunnel) => ({ tunnel, eligible: eligibleSections(tunnel.id) }));
  if (!perTunnel.some((t) => t.eligible.length)) {
    return {
      options: cellTunnels.flatMap((tunnel) =>
        allSections(tunnel.id).map((section) => ({ tunnel, section }))
      ),
      gated: true,
    };
  }
  return {
    options: perTunnel.flatMap(({ tunnel, eligible }) => {
      if (eligible.length) return eligible.map((section) => ({ tunnel, section }));
      // This position offers nothing, and on a hybrid the row's OTHER position
      // might — but the accessory's own section still stands as an option on
      // its own row, so the default stays in place beside the original instead
      // of moving the copy to the back-to-back tunnel (#2512 round 2: the
      // requester chose legacy's duplicate-in-place over either alternative).
      if (tunnel.id === source?.tunnelId && source.section)
        return [{ tunnel, section: source.section }];
      return [];
    }),
    gated: false,
  };
}

/**
 * Which option a row is pointing at: the user's pick, else the accessory's own
 * section when this row offers it (legacy's `selected_section`, so the copy
 * lands beside the original), else — on a hybrid row, which spans two
 * positions — anything in the accessory's OWN position, else the first option.
 *
 * The own-position step is what legacy did by construction, since a row there
 * was a single tunnel: an accessory whose own section the CMS list does not
 * allow (a UV-C in a plenum, which `InsertStrip` deliberately permits) still
 * duplicates within its own tunnel rather than jumping to the back-to-back
 * one. `cellOptions` keeps the accessory's own section on the row for the case
 * where its position offers nothing else, so this never falls past it.
 */
export function resolveCellOption<T extends { id: number }, S extends { id: number }>(
  options: ICellOption<T, S>[],
  selection: { picked?: number; sourceSectionId?: number | null; sourceTunnelId?: number | null }
): ICellOption<T, S> | null {
  if (!options.length) return null;
  const { picked } = selection;
  if (picked != null) {
    const hit = options.find((o) => o.section.id === picked);
    if (hit) return hit;
  }
  return (
    options.find((o) => o.section.id === selection.sourceSectionId) ??
    options.find((o) => o.tunnel.id === selection.sourceTunnelId) ??
    options[0]
  );
}

/**
 * Which ROW is active when the popup opens, and after every click — the row
 * DUPLICATE commits without the user choosing one.
 *
 * It is a row, not a tunnel. Resolving the active row from a tunnel id meant
 * that when a row resolved to its OTHER position — which happens the moment
 * the accessory's own section is filtered out by `csa_available_sections` —
 * no row matched, nothing was highlighted, and DUPLICATE silently did nothing
 * (#2512 review, round 1).
 *
 * **The accessory's own row is always the active one**, gated or not, which is
 * legacy's `selected_tunnel = section.tunnel_id` — `duplicate_accessory.js`
 * sets `.active` regardless of `disable`, and a disabled row's select carries
 * the source section, so DUPLICATE lands the copy in place beside the
 * original with its mounting kept. A gated row giving way to the first row
 * that CAN take the accessory moved the copy to a tunnel the user never
 * picked, on standard AHUs as well as hybrids (#2512 round 2; the requester
 * chose in-place over both alternatives).
 */
export function resolveActiveCell<T extends { id: number }>(
  cells: ITunnelCell<T>[],
  selection: {
    picked?: string | null;
    sourceTunnelId?: number | null;
    isGated: (cell: ITunnelCell<T>) => boolean;
  }
): string | null {
  // A pick can only be a row the user could click, so a gated one is stale —
  // the csa gate landed after they chose it.
  const picked = cells.find((c) => c.cell === selection.picked && !selection.isGated(c));
  if (picked) return picked.cell;
  const source = cells.find((c) => c.tunnels.some((t) => t.id === selection.sourceTunnelId));
  if (source) return source.cell;
  // Only when the accessory's own row is not in the list at all (its tunnel
  // has gone from the board under the popup).
  return cells.find((c) => !selection.isGated(c))?.cell ?? null;
}
